import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import type { MessengerExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { URLTriggerModal } from '../components/urlTriggerModal';

type Route = {
  urlPrefix: string;
  nextNodeId: string;
};

export type MessengerURLPayload = {
  defaultNextNodeId: string;
  routes: Route[];
};

type URLTriggerNodeAttributes = GenericNodeAttributes<
  MessengerURLPayload,
  MessengerExtraData
>;
type URLTriggerNodeProps = GenericNodeProps<
  MessengerURLPayload,
  MessengerExtraData
>;

const translatePayload: URLTriggerNodeAttributes['translatePayload'] = (
  node,
) => {
  const { defaultNextNodeId, routes } = node.payload;

  const urlTriggerConnections = routes.map(({ nextNodeId, urlPrefix }) => ({
    id: nextNodeId,
    label: urlPrefix,
    connection: nextNodeId,
  }));

  return [
    {
      id: uuid(),
      label: 'Default',
      connection: defaultNextNodeId || uuid(),
    },
    ...urlTriggerConnections,
  ];
};

const updateConnection: URLTriggerNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const { payload } = node;
  const { routes } = payload;

  const newNode = { ...node, payload: { ...payload } };
  if (connection.connectionLabel === 'Default') {
    newNode.payload.defaultNextNodeId = connectionId;
  } else if (connection.connectionLabel && connection.connectionLabel) {
    const routeIndex = routes.findIndex(
      (route) => route.urlPrefix === connection.connectionLabel,
    );

    if (routeIndex !== -1) {
      newNode.payload.routes = [...routes];
      newNode.payload.routes[routeIndex] = {
        ...routes[routeIndex],
        nextNodeId: connectionId,
      };
    }
  }

  return newNode;
};

export const URLTriggerNodeAttributes: URLTriggerNodeAttributes = {
  iconName: 'url-trigger',
  label: 'URL Trigger',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: URLTriggerNode,
  ActionModal: URLTriggerModal,
};

export function URLTriggerNode({ node }: URLTriggerNodeProps): JSX.Element {
  const { payload } = node;
  const { routes } = payload;

  const urlCount = routes.length;
  const urlText = urlCount === 1 ? 'URL' : 'URLs';

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="semibold" text={`${urlCount} ${urlText}`} />
          </TextEllipsis>
        ),
        [urlCount, urlText],
      )}
    </TextContent>
  );
}
